import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * The Google Identity Services script (accounts.google.com/gsi/client) is loaded
 * with `async defer` in index.html, so it can still be loading (or blocked by an
 * ad-blocker / slow network) by the time an Angular component tries to call
 * `google.accounts.id.initialize(...)`. That race condition is the most common
 * reason "Sign in with Google" works sometimes and silently does nothing other
 * times. This service exposes a single promise every component can await before
 * touching the `google` global, so we only ever initialize once the SDK is
 * actually ready.
 */
@Injectable({
  providedIn: 'root',
})
export class GoogleAuthService {
  private platformId = inject(PLATFORM_ID);
  private sdkReady: Promise<void> | null = null;

  waitForSdk(timeoutMs: number = 10000): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return Promise.resolve();
    }

    if (this.sdkReady) {
      return this.sdkReady;
    }

    this.sdkReady = new Promise<void>((resolve, reject) => {
      const startedAt = Date.now();

      const check = () => {
        const g = (window as any).google;
        if (g?.accounts?.id) {
          resolve();
          return;
        }

        if (Date.now() - startedAt > timeoutMs) {
          reject(new Error('Google Identity Services failed to load in time.'));
          return;
        }

        setTimeout(check, 100);
      };

      check();
    });

    return this.sdkReady;
  }
}
